import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OptimizationResults = ({ results, onApproveVoyage, onExportResults }) => {
  const [selectedComparison, setSelectedComparison] = useState('emissions');

  // Mock optimization results if none provided
  const mockResults = results || {
    optimizedRoute: {
      id: 'optimized-1',
      name: 'AI Optimized Route',
      confidence: 94,
      improvements: {
        fuelSavings: 165,
        emissionsReduction: 520,
        costSavings: 26000,
        timeReduction: 0.6
      }
    },
    recommendations: [
      {
        type: 'speed',
        title: 'Optimal Speed Profile',
        description: 'Reduce speed to 12.5 knots for first 60% of voyage, then increase to 14 knots',
        impact: 'Saves 145 MT fuel, reduces emissions by 456 MT CO₂',
        priority: 'high',
        icon: 'Gauge'
      },
      {
        type: 'route',
        title: 'Weather Routing Adjustment',
        description: 'Slight northern deviation to avoid adverse weather conditions',
        impact: 'Reduces voyage time by 8 hours, saves 20 MT fuel',
        priority: 'medium',
        icon: 'Cloud'
      },
      {
        type: 'fuel',
        title: 'Alternative Fuel Consideration',
        description: 'Consider LNG for 30% emissions reduction on this route',
        impact: 'Potential 1,176 MT CO₂ reduction, requires fuel availability check',
        priority: 'low',
        icon: 'Fuel'
      }
    ],
    complianceAnalysis: {
      ciiRating: 'B+',
      euaRequired: 3400,
      euaCost: 170000,
      fueleuCompliance: 'Compliant',
      riskLevel: 'Low'
    },
    alternatives: [
      {
        id: 'alt-1',
        name: 'Eco Route',
        fuelConsumption: 1080,
        emissions: 3400,
        duration: 15.8,
        cost: 170000,
        ciiRating: 'A'
      },
      {
        id: 'alt-2',
        name: 'Fast Route',
        fuelConsumption: 1380,
        emissions: 4347,
        duration: 12.4,
        cost: 217350,
        ciiRating: 'C'
      }
    ]
  };

  const data = mockResults;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-success/10';
      default: return 'bg-muted/10';
    }
  };

  const getCiiRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'text-success';
      case 'B+': case 'B': return 'text-accent';
      case 'C': return 'text-warning';
      case 'D': case 'E': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const comparisonMetrics = {
    emissions: {
      label: 'CO₂ Emissions (MT)',
      optimized: Math.round(3400),
      baseline: Math.round(3920),
      improvement: -520
    },
    fuel: {
      label: 'Fuel Consumption (MT)',
      optimized: Math.round(1080),
      baseline: Math.round(1245),
      improvement: -165
    },
    cost: {
      label: 'EUA Cost ($)',
      optimized: Math.round(170000),
      baseline: Math.round(196000),
      improvement: -26000
    },
    time: {
      label: 'Voyage Duration (days)',
      optimized: 13.6,
      baseline: 14.2,
      improvement: -0.6
    }
  };

  if (!results) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1} />
          <h3 className="text-lg font-medium text-foreground mb-2">No Optimization Results</h3>
          <p className="text-sm text-muted-foreground">
            Configure your voyage parameters and click "Optimize Route" to see AI-powered recommendations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Optimization Summary */}
      <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Target" size={20} className="text-success" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Optimization Results</h2>
                <p className="text-sm text-muted-foreground">
                  AI confidence: {data?.optimizedRoute?.confidence}% • Generated {new Date()?.toLocaleTimeString()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={onExportResults}
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export Results
              </Button>
              <Button
                onClick={onApproveVoyage}
                iconName="Check"
                iconPosition="left"
                className="bg-success hover:bg-success/90 text-success-foreground"
              >
                Approve Voyage
              </Button>
            </div>
          </div>
        </div>

        {/* Key Improvements */}
        <div className="p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Key Improvements</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
              <Icon name="Fuel" size={24} className="text-success mx-auto mb-2" strokeWidth={2} />
              <div className="text-2xl font-bold text-success">
                {data?.optimizedRoute?.improvements?.fuelSavings} MT
              </div>
              <div className="text-xs text-muted-foreground">Fuel Savings</div>
            </div>
            
            <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
              <Icon name="Leaf" size={24} className="text-success mx-auto mb-2" strokeWidth={2} />
              <div className="text-2xl font-bold text-success">
                {data?.optimizedRoute?.improvements?.emissionsReduction} MT
              </div>
              <div className="text-xs text-muted-foreground">CO₂ Reduction</div>
            </div>
            
            <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
              <Icon name="DollarSign" size={24} className="text-success mx-auto mb-2" strokeWidth={2} />
              <div className="text-2xl font-bold text-success">
                ${(data?.optimizedRoute?.improvements?.costSavings / 1000)?.toFixed(0)}K
              </div>
              <div className="text-xs text-muted-foreground">Cost Savings</div>
            </div>
            
            <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
              <Icon name="Clock" size={24} className="text-success mx-auto mb-2" strokeWidth={2} />
              <div className="text-2xl font-bold text-success">
                {data?.optimizedRoute?.improvements?.timeReduction?.toFixed(1)}d
              </div>
              <div className="text-xs text-muted-foreground">Time Saved</div>
            </div>
          </div>
        </div>
      </div>
      {/* Detailed Comparison */}
      <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Detailed Comparison</h3>
          <p className="text-sm text-muted-foreground">Optimized route vs. baseline scenario</p>
        </div>

        <div className="p-6">
          <div className="flex space-x-2 mb-6">
            {Object.entries(comparisonMetrics)?.map(([key, metric]) => (
              <button
                key={key}
                onClick={() => setSelectedComparison(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-maritime ${
                  selectedComparison === key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                {metric?.label?.split(' ')?.[0]}
              </button>
            ))}
          </div>

          <div className="bg-muted/50 rounded-lg p-6">
            <div className="text-center mb-4">
              <h4 className="text-lg font-semibold text-foreground">
                {comparisonMetrics?.[selectedComparison]?.label}
              </h4>
            </div>

            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-sm text-muted-foreground mb-2">Baseline</div>
                <div className="text-2xl font-bold text-foreground">
                  {selectedComparison === 'cost' 
                    ? `$${comparisonMetrics?.[selectedComparison]?.baseline?.toLocaleString()}`
                    : comparisonMetrics?.[selectedComparison]?.baseline?.toLocaleString()
                  }
                </div>
              </div>
              
              <div>
                <div className="text-sm text-muted-foreground mb-2">Improvement</div>
                <div className="text-2xl font-bold text-success">
                  {comparisonMetrics?.[selectedComparison]?.improvement > 0 ? '+' : ''}
                  {selectedComparison === 'cost' 
                    ? `$${comparisonMetrics?.[selectedComparison]?.improvement?.toLocaleString()}`
                    : comparisonMetrics?.[selectedComparison]?.improvement?.toLocaleString()
                  }
                </div>
              </div>
              
              <div>
                <div className="text-sm text-muted-foreground mb-2">Optimized</div>
                <div className="text-2xl font-bold text-primary">
                  {selectedComparison === 'cost' 
                    ? `$${comparisonMetrics?.[selectedComparison]?.optimized?.toLocaleString()}`
                    : comparisonMetrics?.[selectedComparison]?.optimized?.toLocaleString()
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* AI Recommendations */}
      <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">AI Recommendations</h3>
          <p className="text-sm text-muted-foreground">Actionable insights for voyage optimization</p>
        </div>

        <div className="p-6 space-y-4">
          {data?.recommendations?.map((rec, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 ${getPriorityBg(rec?.priority)} border-opacity-20`}
            >
              <div className="flex items-start space-x-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getPriorityBg(rec?.priority)}`}>
                  <Icon name={rec?.icon} size={20} className={getPriorityColor(rec?.priority)} strokeWidth={2} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-medium text-foreground">{rec?.title}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getPriorityBg(rec?.priority)} ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority} priority
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">{rec?.description}</p>
                  <p className="text-sm text-foreground font-medium">{rec?.impact}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Compliance Analysis */}
      <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Compliance Analysis</h3>
          <p className="text-sm text-muted-foreground">Regulatory compliance assessment</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">CII Rating</div>
              <div className={`text-2xl font-bold ${getCiiRatingColor(data?.complianceAnalysis?.ciiRating)}`}>
                {data?.complianceAnalysis?.ciiRating}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">EUA Required</div>
              <div className="text-2xl font-bold text-foreground">
                {data?.complianceAnalysis?.euaRequired?.toLocaleString()}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">FuelEU Status</div>
              <div className="text-2xl font-bold text-success">
                {data?.complianceAnalysis?.fueleuCompliance}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">Risk Level</div>
              <div className={`text-2xl font-bold ${getRiskColor(data?.complianceAnalysis?.riskLevel)}`}>
                {data?.complianceAnalysis?.riskLevel}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizationResults;